import type { ReactNode } from 'react';

import { FlaskConical, HelpCircle, Package, Wrench } from 'lucide-react';
import type { RenderElementProps } from 'slate-react';

import { cn } from '@/lib/utils';

import { ContextSelectionType } from '@/components/chat/ContextSelectionContext';

import { iconColorClassMap } from './iconColorClassMap';

type SlateSpanAttributes = RenderElementProps['attributes'] & {
  className?: string;
};

const iconByType = {
  [ContextSelectionType.Experiment]: FlaskConical,
  [ContextSelectionType.Product]: Package,
  [ContextSelectionType.Tool]: Wrench,
} as const;

const contextTypeLabel: Record<ContextSelectionType, string> = {
  [ContextSelectionType.Product]: 'Product',
  [ContextSelectionType.Tool]: 'Tool',
  [ContextSelectionType.Experiment]: 'Experiment',
};

export type ContextInlineTokenProps = {
  label: string;
  type: ContextSelectionType;
  slateAttributes: SlateSpanAttributes;
  children: ReactNode;
};

export const ContextInlineToken = ({
  label,
  type,
  slateAttributes,
  children,
}: ContextInlineTokenProps) => {
  const Icon = iconByType[type as keyof typeof iconByType] ?? HelpCircle;
  const iconColorClass =
    iconColorClassMap[type as keyof typeof iconColorClassMap] ??
    'text-muted-foreground';
  const iconAriaLabel = contextTypeLabel[type] ?? 'Context';
  const { className: slateClassName, ...restSlateAttributes } = slateAttributes;

  return (
    <span
      {...restSlateAttributes}
      contentEditable={false}
      data-testid="stella-context-inline-token"
      className={cn(
        slateClassName,
        'inline-flex max-w-full min-w-[80px] shrink overflow-hidden items-center gap-0.5 align-middle'
      )}
    >
      <span className="flex min-w-0 w-full max-w-full items-center gap-0.5 text-sm font-semibold text-popover-foreground">
        <Icon
          className={cn('h-4 w-4 shrink-0', iconColorClass)}
          aria-label={iconAriaLabel}
        />
        <span
          className="min-w-0 truncate"
          data-testid="stella-context-inline-token-label"
        >
          {label}
        </span>
      </span>
      {children}
    </span>
  );
};
